import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    App,
    Button,
    Card,
    ConfigProvider,
    Empty,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    theme,
} from "antd";
import {DeleteOutlined, EditOutlined, ExclamationCircleFilled, LockOutlined, MailOutlined, MessageOutlined, TeamOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import AxiosServices from "../../Services/axiosServices";
import {useAuth} from "../../config/AuthContext";

const ACCENT = '#203923';
const {Paragraph} = Typography;

type AdminUser = {
    idt: number;
    username?: string;
    email?: string;
    role?: string;
    firstName?: string;
    lastName?: string;
    verified?: boolean;
    hideName?: boolean;
    hideEmail?: boolean;
    Title?: {code: string; libelle?: string};
    nbBets?: number;
    totalPoints?: number;
};

type Props = {
    readOnly?: boolean;
};

const AdminUsers: FC<Props> = ({readOnly = false}) => {
    const {isAdmin, isLoggedIn, authUser} = useAuth();
    const {message} = App.useApp();

    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState<AdminUser | null>(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingIdt, setDeletingIdt] = useState<number | null>(null);
    const [resendingIdt, setResendingIdt] = useState<number | null>(null);
    const [chattingIdt, setChattingIdt] = useState<number | null>(null);
    const [editForm] = Form.useForm();
    const navigate = useNavigate();

    const startChatWith = async (user: AdminUser) => {
        if (authUser?.idt === user.idt) return;
        setChattingIdt(user.idt);
        try {
            const ch = await AxiosServices.chatSendRequest(user.idt);
            if (ch?.state === 'ACCEPTED') {
                message.success(`Discussion ouverte avec ${user.username}.`);
            } else if (ch?.state === 'PENDING') {
                message.info(`Demande envoyée à ${user.username}.`);
            }
            navigate('/chat');
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setChattingIdt(null);
        }
    };

    const refresh = useCallback(() => {
        setLoading(true);
        AxiosServices.listUsers()
            .then((data: AdminUser[]) => setUsers(data ?? []))
            .catch((err) => {
                const msg = err?.response?.data?.error ?? err?.message ?? 'Erreur inconnue';
                message.error(`Impossible de charger les utilisateurs : ${msg}`);
            })
            .finally(() => setLoading(false));
    }, [message]);

    useEffect(() => {
        if (!isLoggedIn) return;
        if (!readOnly && !isAdmin) return;
        refresh();
    }, [isLoggedIn, isAdmin, readOnly, refresh]);

    const sortedUsers = useMemo(() => {
        return [...users].sort((a, b) => (a.username ?? '').localeCompare(b.username ?? ''));
    }, [users]);

    if (!isLoggedIn) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Connecte-toi pour accéder à cette page."/>
            </div>
        );
    }

    if (!readOnly && !isAdmin) {
        return (
            <div style={{padding: 24}}>
                <Alert
                    type="error"
                    showIcon
                    icon={<LockOutlined/>}
                    message="Accès refusé"
                    description="Cette page est réservée aux administrateurs."
                />
            </div>
        );
    }

    const openEdit = async (user: AdminUser) => {
        setEditing(user);
        editForm.setFieldsValue({
            email: user.email ?? '',
            role: (user.role ?? 'USER').toUpperCase(),
        });
        const isSelf = authUser?.idt === user.idt;
        if (!user.email && user.hideEmail && !isSelf) {
            try {
                const {email} = await AxiosServices.adminGetUserEmail(user.idt);
                editForm.setFieldsValue({email});
            } catch (err: any) {
                const status = err?.response?.status;
                const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
                message.error(`Échec ${status ?? ''} récupération email : ${serverMsg ?? 'erreur inconnue'}`);
            }
        }
    };

    const closeEdit = () => {
        setEditing(null);
        editForm.resetFields();
    };

    const onEditSubmit = async (values: {email: string; role: string}) => {
        if (!editing) return;
        const isSelfEdit = authUser?.idt === editing.idt;
        const payload = isSelfEdit ? {email: values.email} : values;
        setSavingEdit(true);
        try {
            const updated: AdminUser = await AxiosServices.adminUpdateUser(editing.idt, payload);
            setUsers((prev) => prev.map((u) => (u.idt === editing.idt ? {...u, ...updated} : u)));
            message.success(`Utilisateur ${updated.username ?? editing.username} mis à jour.`);
            closeEdit();
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setSavingEdit(false);
        }
    };

    const onResendVerification = async (user: AdminUser) => {
        setResendingIdt(user.idt);
        try {
            await AxiosServices.adminResendVerification(user.idt);
            message.success(`Mail de vérification renvoyé à ${user.email}.`);
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setResendingIdt(null);
        }
    };

    const performDelete = async (user: AdminUser) => {
        setDeletingIdt(user.idt);
        try {
            await AxiosServices.adminDeleteUser(user.idt);
            setUsers((prev) => prev.filter((u) => u.idt !== user.idt));
            message.success(`Utilisateur ${user.username} supprimé.`);
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setDeletingIdt(null);
        }
    };

    const onDelete = async (user: AdminUser) => {
        const nbMatches = Math.floor((user.nbBets ?? 0) / 2);
        if (nbMatches > 0) {
            Modal.confirm({
                title: `Attention — ${user.username} a parié ${nbMatches} match${nbMatches > 1 ? 's' : ''}`,
                icon: <ExclamationCircleFilled style={{color: '#ff4d4f'}}/>,
                content: `La suppression est définitive : tous ses paris (${user.nbBets ?? 0} entrées) seront aussi détruits. Confirmer ?`,
                okText: 'Supprimer définitivement',
                okType: 'danger',
                cancelText: 'Annuler',
                onOk: () => performDelete(user),
            });
            return;
        }
        await performDelete(user);
    };

    const columns = [
        {
            title: 'Identifiant',
            dataIndex: 'username',
            key: 'username',
            sorter: (a: AdminUser, b: AdminUser) => (a.username ?? '').localeCompare(b.username ?? ''),
            render: (name: string, u: AdminUser) => (
                <Space size={6}>
                    <span>{name}</span>
                    {authUser?.idt === u.idt && <Tag color="geekblue">Vous</Tag>}
                </Space>
            ),
        },
        {
            title: 'Nom complet',
            key: 'fullname',
            render: (_: any, u: AdminUser) => {
                const isSelf = authUser?.idt === u.idt;
                if (u.hideName && !isSelf) return <span style={{opacity: 0.6, fontStyle: 'italic'}}>(masqué)</span>;
                return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—';
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (email: string, u: AdminUser) => {
                const isSelf = authUser?.idt === u.idt;
                if (!email && u.hideEmail && !isSelf) {
                    return <span style={{opacity: 0.6, fontStyle: 'italic'}}>(masqué)</span>;
                }
                return email || '—';
            },
        },
        {
            title: 'Rôle',
            key: 'role',
            render: (_: any, u: AdminUser) => {
                const role = (u.role ?? 'USER').toUpperCase();
                return <Tag color={role === 'ADMIN' ? 'gold' : 'blue'}>{role}</Tag>;
            },
            filters: [
                {text: 'Admin', value: 'ADMIN'},
                {text: 'User', value: 'USER'},
            ],
            onFilter: (val: any, u: AdminUser) => (u.role ?? '').toUpperCase() === val,
        },
        {
            title: 'Vérifié',
            key: 'verified',
            render: (_: any, u: AdminUser) => (
                <Space size={4}>
                    {u.verified ? <Tag color="green">Oui</Tag> : <Tag color="red">Non</Tag>}
                    {!u.verified && !readOnly && (
                        <Popconfirm
                            title="Renvoyer le mail d'inscription ?"
                            description={`Un nouveau lien de vérification sera envoyé à ${u.email}.`}
                            okText="Renvoyer"
                            cancelText="Annuler"
                            onConfirm={() => onResendVerification(u)}
                        >
                            <Tooltip title="Renvoyer le mail de vérification">
                                <Button
                                    size="small"
                                    icon={<MailOutlined/>}
                                    loading={resendingIdt === u.idt}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
            filters: [
                {text: 'Vérifié', value: true},
                {text: 'Non vérifié', value: false},
            ],
            onFilter: (val: any, u: AdminUser) => !!u.verified === val,
        },
        {
            title: 'Matchs pariés',
            key: 'nbMatches',
            align: 'right' as const,
            render: (_: any, u: AdminUser) => Math.floor((u.nbBets ?? 0) / 2),
            sorter: (a: AdminUser, b: AdminUser) => (a.nbBets ?? 0) - (b.nbBets ?? 0),
        },
        {
            title: 'Points',
            key: 'totalPoints',
            align: 'right' as const,
            render: (_: any, u: AdminUser) => u.totalPoints ?? 0,
            sorter: (a: AdminUser, b: AdminUser) => (a.totalPoints ?? 0) - (b.totalPoints ?? 0),
        },
    ];
    columns.push({
        title: 'Actions',
        key: 'actions',
        align: 'center' as const,
        render: (_: any, u: AdminUser) => {
            const isSelf = authUser?.idt === u.idt;
            return (
                <Space size={4}>
                    {!isSelf && (
                        <Tooltip title={`Discuter avec ${u.username}`}>
                            <Button
                                size="small"
                                icon={<MessageOutlined/>}
                                loading={chattingIdt === u.idt}
                                onClick={() => startChatWith(u)}
                            />
                        </Tooltip>
                    )}
                    {!readOnly && (
                        <Tooltip title="Modifier l'utilisateur">
                            <Button
                                size="small"
                                icon={<EditOutlined/>}
                                onClick={() => openEdit(u)}
                            />
                        </Tooltip>
                    )}
                    {!readOnly && !isSelf && (
                        <Popconfirm
                            title="Supprimer cet utilisateur ?"
                            description={`Cette action est définitive : ${u.username}.`}
                            okText="Supprimer"
                            okButtonProps={{danger: true, loading: deletingIdt === u.idt}}
                            cancelText="Annuler"
                            onConfirm={() => onDelete(u)}
                        >
                            <Tooltip title="Supprimer l'utilisateur">
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined/>}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            );
        },
    } as any);

    return (
        <div style={{padding: 16, height: '100%', boxSizing: 'border-box', overflow: 'hidden', background: ACCENT}}>
            <Card
                styles={{
                    header: {background: ACCENT, color: '#fff'},
                    body: {background: ACCENT, color: '#fff'},
                }}
                style={{background: ACCENT, borderColor: ACCENT}}
                title={
                    <Space>
                        <TeamOutlined/>
                        <span>{readOnly ? 'Utilisateurs' : 'Administration — Utilisateurs'}</span>
                    </Space>
                }
            >
                <Paragraph style={{marginBottom: 16, color: '#cfd8d3'}}>
                    {readOnly
                        ? 'Annuaire des comptes inscrits, leur nombre de matchs pariés et leur total de points.'
                        : "Liste en lecture seule. Utilise « Modifier » pour changer l'email ou le rôle, et « Supprimer » pour retirer un compte (et ses paris)."}
                </Paragraph>
                <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
                    <Table
                        dataSource={sortedUsers}
                        columns={columns}
                        rowKey={(u) => u.idt.toString()}
                        loading={loading}
                        pagination={false}
                        size="small"
                        sticky
                        scroll={{y: 'calc(100vh - 410px)'}}
                    />
                </ConfigProvider>

                {!readOnly && <Modal
                    open={!!editing}
                    title={editing ? `Modifier ${editing.username}` : 'Modifier'}
                    onCancel={closeEdit}
                    onOk={() => editForm.submit()}
                    okText="Enregistrer"
                    cancelText="Annuler"
                    confirmLoading={savingEdit}
                    destroyOnClose
                >
                    <Form
                        form={editForm}
                        layout="vertical"
                        onFinish={onEditSubmit}
                        requiredMark={false}
                    >
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                {required: true, message: 'Email requis'},
                                {type: 'email', message: 'Email invalide'},
                            ]}
                        >
                            <Input/>
                        </Form.Item>
                        <Form.Item
                            name="role"
                            label="Rôle"
                            rules={[{required: true, message: 'Rôle requis'}]}
                            extra={editing && authUser?.idt === editing.idt ? 'Tu ne peux pas modifier ton propre rôle.' : undefined}
                        >
                            <Select
                                disabled={!!editing && authUser?.idt === editing.idt}
                                options={[
                                    {value: 'USER', label: 'User'},
                                    {value: 'ADMIN', label: 'Admin'},
                                ]}
                            />
                        </Form.Item>
                    </Form>
                </Modal>}
            </Card>
        </div>
    );
};

export default AdminUsers;
